import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';

// Scroll-triggered reveal. Wrap any block and it fades + slides into
// view the first time it intersects the viewport. Once revealed it
// stays revealed — we disconnect the observer so re-scrolling past a
// section doesn't re-trigger the animation.
//
// Props:
//   direction — 'up' (default), 'down', 'left', 'right'
//   delay    — ms before the transition starts (stagger siblings)
//   distance — offset in px before revealing (default 40)
//   duration — ms of the fade+slide (default 700)
//   threshold / rootMargin — passed through to IntersectionObserver
//   sx        — additional MUI sx merged onto the wrapper
//
// All children are rendered immediately so SSR / search engines see
// everything; we only toggle opacity + transform so layout doesn't jump.
const Reveal = ({
  children,
  direction = 'up',
  delay = 0,
  distance = 40,
  duration = 700,
  threshold = 0.12,
  rootMargin = '0px 0px -60px 0px',
  sx,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Respect reduced-motion and environments without IO (very old
    // browsers) by just showing the content with no animation.
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  const hiddenTransform = (() => {
    switch (direction) {
      case 'left':  return `translate3d(-${distance}px, 0, 0)`;
      case 'right': return `translate3d(${distance}px, 0, 0)`;
      case 'down':  return `translate3d(0, -${distance}px, 0)`;
      case 'up':
      default:      return `translate3d(0, ${distance}px, 0)`;
    }
  })();

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate3d(0, 0, 0)' : hiddenTransform,
        transition: `opacity ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
